import { Link } from 'react-router-dom'
import { GraphUp, WarningTriangle, CheckCircle, XmarkCircle } from 'iconoir-react'
import DashboardTabs from 'app/components/analytics/DashboardTabs'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import TableLayout from 'app/components/table/TableLayout'
import type { UseConversionMonitoringReturn } from './useConversionMonitoring'

export type ConversionMonitoringComponentProps = UseConversionMonitoringReturn

export function ConversionMonitoringComponent(props: ConversionMonitoringComponentProps) {
  const {
    metrics,
    activeTab,
    handleTabChange,
    focusedContact,
    openContactDrawer,
    closeDrawerHandler,
    conversionTypeData,
    recentAttemptsData,
    recentAttemptsColumns,
    setFocusedContact,
  } = props

  return (
    <>
      <PageScroll
        contentClassName="py-6"
        header={
          <>
            <PageHeader
              title="Conversion Monitoring Dashboard"
              subTitle="Track Google Ads conversion performance and identify issues"
            />
            <DashboardTabs
              openTab={activeTab}
              onSetOpenTab={handleTabChange}
              tabOptions={[`Overview`, `Recent Attempts`]}
            />
          </>
        }
      >
        {activeTab === `Overview` && (
          <div className="space-y-6">
            {metrics.alertsTriggered.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="mb-3 flex items-center space-x-2">
                  <WarningTriangle className="h-5 w-5 text-red-600" />
                  <h3 className="text-lg font-semibold text-red-800">Active Alerts</h3>
                </div>
                <div className="space-y-2">
                  {metrics.alertsTriggered.map((alert, index) => (
                    <div key={index} className="font-medium text-red-700">
                      • {alert}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-lg bg-surface p-6 shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <GraphUp className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-subtle">Total Attempts (7 days)</p>
                    <p className="text-2xl font-semibold text-strong">{metrics.totalAttempts}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-surface p-6 shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-subtle">Success Rate</p>
                    <p className="text-2xl font-semibold text-green-600">{metrics.successRate}%</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-surface p-6 shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <XmarkCircle className="h-8 w-8 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-subtle">Failure Rate</p>
                    <p className="text-2xl font-semibold text-red-600">{metrics.failureRate}%</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-surface p-6 shadow">
              <h3 className="mb-4 text-lg font-semibold text-strong">Conversion Performance by Type</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-default">
                      <th className="px-4 py-3 text-left font-semibold text-strong">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-strong">Total</th>
                      <th className="px-4 py-3 text-left font-semibold text-strong">Sent</th>
                      <th className="px-4 py-3 text-left font-semibold text-strong">Failed</th>
                      <th className="px-4 py-3 text-left font-semibold text-strong">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversionTypeData.map((row) => (
                      <tr key={row.type} className="border-b border-subtle">
                        <td className="px-4 py-3">{row.type}</td>
                        <td className="px-4 py-3">{row.total}</td>
                        <td className="px-4 py-3 text-green-600">{row.sent}</td>
                        <td className="px-4 py-3 text-red-600">{row.failed}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`font-semibold ${
                              row.successRate >= 80
                                ? `text-green-600`
                                : row.successRate >= 60
                                  ? `text-yellow-600`
                                  : `text-red-600`
                            }`}
                          >
                            {row.successRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === `Recent Attempts` && (
          <div className="flex h-full flex-col">
            <TableLayout data={recentAttemptsData} columns={recentAttemptsColumns} hideControls={false} />
          </div>
        )}
      </PageScroll>

      <ContactDrawer
        openDrawer={openContactDrawer}
        contact={focusedContact}
        onClose={closeDrawerHandler}
        onChange={(updatedContact) => setFocusedContact(updatedContact)}
      />
    </>
  )
}
